import LegalLayout, { LegalSection, LegalList, ContactBlock } from "@/pages/legal/LegalLayout";

const TOC = [
  { id: "commitment", label: "Our commitment" },
  { id: "encryption", label: "Encryption" },
  { id: "auth", label: "Secure authentication" },
  { id: "access", label: "Access controls" },
  { id: "infra", label: "Infrastructure & monitoring" },
  { id: "handling", label: "Responsible data handling" },
  { id: "recommendations", label: "Your security tips" },
  { id: "report", label: "Report a security issue" },
  { id: "no-guarantee", label: "No absolute guarantee" },
  { id: "contact", label: "Contact" },
];

export default function SecurityPage() {
  return (
    <LegalLayout
      testid="security-page"
      title="Security"
      intro="Relyn is built to help you understand yourself and grow. That work depends on trust — and trust starts with security."
      effectiveDate="December 1, 2026"
      lastUpdated="December 1, 2026"
      toc={TOC}
    >
      <LegalSection id="commitment" title="Our commitment">
        <p>
          Relyn is committed to protecting the information you share with us. We use
          modern security engineering practices and work continuously to improve them.
          Security is a first-class product concern at Relyn, not an afterthought.
        </p>
      </LegalSection>

      <LegalSection id="encryption" title="Encryption in transit and at rest">
        <p>
          Data traveling between your device and Relyn is encrypted using industry-standard
          protocols (TLS 1.2+). Sensitive user content — including journal entries, mood
          logs, relationship notes, goals, and Leo conversations — is encrypted at rest
          on our infrastructure providers, where supported.
        </p>
      </LegalSection>

      <LegalSection id="auth" title="Secure authentication">
        <LegalList
          items={[
            "Passwords are hashed using strong, salted, one-way algorithms — never stored in plain text.",
            "We support modern account protections and are rolling out multi-factor authentication for accounts that opt in.",
            "Suspicious sign-in attempts are throttled and, where appropriate, blocked.",
          ]}
        />
      </LegalSection>

      <LegalSection id="access" title="Access controls">
        <p>
          Access to production systems and user data is restricted to a small number of
          authorized personnel. Access is granted on a least-privilege basis, requires
          strong authentication, and is audited.
        </p>
      </LegalSection>

      <LegalSection id="infra" title="Infrastructure and monitoring">
        <LegalList
          items={[
            "Relyn runs on reputable cloud infrastructure providers with strong security certifications.",
            "We monitor our services for availability, performance, and anomalous activity around the clock.",
            "Backups are performed regularly and stored securely so we can restore service if something goes wrong.",
            "We patch dependencies and infrastructure on a regular cadence.",
          ]}
        />
      </LegalSection>

      <LegalSection id="handling" title="Responsible data handling">
        <p>
          We only collect the information we need to run Relyn, we use it only for the
          purposes described in our Privacy Policy, and we retain it only as long as
          necessary. When you delete your account, we delete your personal information
          in accordance with our retention schedule and applicable law.
        </p>
      </LegalSection>

      <LegalSection id="recommendations" title="Recommendations for your account">
        <p>Security is a shared effort. To keep your account safe:</p>
        <LegalList
          items={[
            "Use a strong, unique password for your Relyn account.",
            "Enable multi-factor authentication when available.",
            "Keep your device's operating system and Relyn app up to date.",
            "Do not share your login credentials with anyone.",
            "Sign out on shared devices when you're done.",
            "If you suspect your account has been accessed by someone else, contact us immediately.",
          ]}
        />
      </LegalSection>

      <LegalSection id="report" title="Reporting a security issue">
        <p>
          If you believe you've found a security vulnerability in Relyn, please contact
          us at{" "}
          <a
            href="mailto:support@relynapp.com"
            className="text-[#5aa8ff] hover:text-white underline decoration-[#2D8CFF]/40 underline-offset-4"
            data-testid="security-report-email"
          >
            support@relynapp.com
          </a>{" "}
          with a clear description of the issue and, if possible, steps to reproduce.
          Please give us reasonable time to investigate and remediate before public
          disclosure. We appreciate the security community and will acknowledge
          researchers who report issues responsibly.
        </p>
      </LegalSection>

      <LegalSection id="no-guarantee" title="No absolute guarantee">
        <p>
          <strong>No online platform can guarantee absolute security.</strong> While we
          use industry-standard safeguards and continuously invest in protecting your
          data, no system is fully immune to risk. By using Relyn, you acknowledge and
          accept this inherent limitation.
        </p>
      </LegalSection>

      <LegalSection id="contact" title="Contact">
        <p>
          For general security questions or feedback, please reach us at{" "}
          <a href="mailto:support@relynapp.com" className="text-[#5aa8ff] hover:text-white underline decoration-[#2D8CFF]/40 underline-offset-4">
            support@relynapp.com
          </a>
          .
        </p>
        <ContactBlock />
      </LegalSection>
    </LegalLayout>
  );
}
